import { useEffect, useRef } from 'react';
import { isCurrentNavObj, type PatientDoc } from '@usacsmideo/types';
import { setInitialUnifiedNavObject } from '../data/navigation/setInitialUnifiedNavObj';

export const NAV_UPDATED_MESSAGE =
  "Your app's navigation has been updated. You may wish to review previously completed sections to ensure all information is accurate.";

/** Attempt key for a navObj whose `version` is not a string. */
const UNVERSIONED = '<unversioned>';

interface UseNavObjVersionUpdateArgs {
  patientId: string;
  patientInfo: PatientDoc | undefined;
  /** Extra readiness gate from the caller (the index route waits for the chart). */
  enabled: boolean;
}

/**
 * Brings a stale navObj (version behind `UNIFIED_NAV_OBJ_VERSION`) up to date
 * by calling `setInitialNavObject`, which reconciles and writes it on the
 * server in one transaction (CU-17tm466h63q).
 *
 * - **One call per stale version per mount.** The effect runs on every
 *   patient-doc snapshot; without a guard each snapshot fired another call
 *   (MIDEO-UNIFIED-72, "Rate limit hit: setInitialNavObject").
 * - ⚠️ **The attempted mark is cleared only when a CURRENT navObj is seen,
 *   never on success.** The call's response races the listener: a stale
 *   snapshot can arrive after success, and clearing then would fire a second
 *   call and a second alert. If a whole-map writer that read earlier later
 *   puts the same stale version back, the current snapshot in between has
 *   cleared the mark, so exactly one more call runs.
 * - **A failure is not retried on this mount** (no request storm offline). A
 *   refresh retries; the patient keeps their existing navObj meanwhile.
 * - ⚠️ **No client navObj write.** The client used to re-align the CF's result
 *   against its CACHED chart and write the whole map back. That write could
 *   revert other writers' navObj changes, and the server already writes it.
 */
export function useNavObjVersionUpdate({ patientId, patientInfo, enabled }: UseNavObjVersionUpdateArgs): void {
  const inFlight = useRef(false);
  const attemptedVersion = useRef<string | null>(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const navObj = patientInfo?.navObj;

  useEffect(() => {
    if (!enabled || !patientId || !navObj) return;
    // Read before the type guard below narrows navObj.
    const version: unknown = (navObj as { version?: unknown }).version;
    if (isCurrentNavObj(navObj)) {
      attemptedVersion.current = null;
      return;
    }
    const key = typeof version === 'string' ? version : UNVERSIONED;
    if (inFlight.current || attemptedVersion.current === key) return;

    inFlight.current = true;
    attemptedVersion.current = key;
    setInitialUnifiedNavObject(patientId)
      .then(() => {
        // Skip the alert if the screen is gone (CU-17tm466h63q, edge case 4).
        if (mounted.current) alert(NAV_UPDATED_MESSAGE);
      })
      .catch((err) => {
        // The data function already captures to Sentry.
        console.error('Error updating nav object version', err);
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [enabled, patientId, navObj]);
}
